import type { CSSProperties } from 'react';

import { CloseGlyph } from '../components/CloseGlyph';
import { CHANNEL_BUTTON_COLORS, channelIconSvg } from './channel-icons';
import { openWidgetChannel } from './adapters';
import type { WidgetChannel } from './types';

type ChannelLauncherProps = {
  channels: WidgetChannel[];
  open: boolean;
  chatOpen: boolean;
  unread: number;
  customLauncherIcon: string | null;
  launcherIconHtml: string;
  animationClass: string;
  onToggleMenu: () => void;
  onOpenWebChat: (channel?: WidgetChannel) => void;
  onClose: () => void;
};

/**
 * Multi-channel launcher: vertical stack of brand circles + WebChat + close.
 * When only WebChat is available, callers should use the classic single launcher.
 */
export function ChannelLauncher({
  channels,
  open,
  chatOpen,
  unread,
  customLauncherIcon,
  launcherIconHtml,
  animationClass,
  onToggleMenu,
  onOpenWebChat,
  onClose,
}: ChannelLauncherProps) {
  const visible = channels.filter(
    (channel) => channel.enabled && channel.available !== false,
  );
  const showStack = open && !chatOpen;

  function handleChannelClick(channel: WidgetChannel) {
    openWidgetChannel(channel, { openWebChat: onOpenWebChat });
  }

  return (
    <div className="rw-channel-launcher" role="group" aria-label="Contact channels">
      {showStack ? (
        <ul className="rw-channel-stack" aria-label="Available channels">
          {visible.map((channel) => (
            <li key={channel.id ?? channel.type}>
              <button
                type="button"
                className={[
                  'rw-channel-btn',
                  channel.type === 'webchat' ? 'rw-channel-btn-webchat' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                title={channel.displayName}
                aria-label={channel.displayName}
                style={
                  {
                    background:
                      channel.type === 'webchat' && customLauncherIcon
                        ? undefined
                        : CHANNEL_BUTTON_COLORS[channel.type],
                    ...(channel.type === 'webchat' && customLauncherIcon
                      ? {
                          backgroundImage: `url(${customLauncherIcon})`,
                          backgroundSize: 'cover',
                        }
                      : {}),
                  } as CSSProperties
                }
                onClick={() => handleChannelClick(channel)}
              >
                {channel.type === 'webchat' && customLauncherIcon ? null : (
                  <span
                    className="rw-channel-btn-icon"
                    dangerouslySetInnerHTML={{
                      __html: channelIconSvg(channel.icon || channel.type),
                    }}
                  />
                )}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div
        className={['rw-launcher-motion', !showStack && !chatOpen ? animationClass : '']
          .filter(Boolean)
          .join(' ')}
      >
        <button
          type="button"
          className={[
            'rw-launcher',
            showStack || chatOpen ? 'rw-launcher-open' : '',
            customLauncherIcon && !showStack && !chatOpen ? 'rw-launcher-image' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => {
            if (chatOpen || showStack) {
              onClose();
              return;
            }
            onToggleMenu();
          }}
          aria-label={
            chatOpen || showStack ? 'Close' : 'Open contact channels'
          }
          aria-expanded={showStack || chatOpen}
          style={
            customLauncherIcon && !showStack && !chatOpen
              ? {
                  backgroundImage: `url(${customLauncherIcon})`,
                  backgroundSize: 'cover',
                }
              : undefined
          }
        >
          {customLauncherIcon && !showStack && !chatOpen ? null : showStack ||
            chatOpen ? (
            <CloseGlyph />
          ) : (
            <span
              className="rw-launcher-icon"
              dangerouslySetInnerHTML={{ __html: launcherIconHtml }}
            />
          )}
          {unread > 0 && !chatOpen && !showStack ? (
            <span className="rw-badge">{unread > 9 ? '9+' : unread}</span>
          ) : null}
        </button>
      </div>
    </div>
  );
}
